import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAgentProfile } from "../hooks/useAgentProfile";
import {
  fetchAgentBookings,
  fetchAgentBookingById,
  fetchAgentBookingCompanyOptions,
  fetchAgentBookingStatusCounts,
  fetchAgentContainerOptions,
  AgentContainerOption,
} from "../services/agentPortalService";
import { fetchMasterContainersForFilter } from "../services/masterContainerService";
import { Booking } from "../types/booking";
import {
  AGENT_BOOKING_STATUS_TABS,
  AGENT_LIST_BOOKING_STATUSES,
} from "../config/agentPortal";
import { parseAgentBookingIdSearch } from "../utils/agentBookingSearch";
import {
  parseUrlHasContainer,
  parseUrlInt,
  parseUrlPickupFilter,
  parseUrlServiceLevel,
  parseUrlStatus,
  setUrlParams,
} from "../utils/agentPortalUrlParams";
import { AgentHasContainerFilter } from "../components/agent/AgentBookingsFilters";
import { AgentPickupDateFilter } from "../utils/agentBookingPickupDate";
import type { ServiceLevel } from "../utils/serviceLevelUtils";

type SearchMode = "none" | "id";

export interface AgentBookingFilterState {
  status: string;
  q: string;
  companyId?: number;
  transportTypeId?: number;
  hasContainer?: AgentHasContainerFilter;
  containerId?: number;
  masterContainerId?: number;
  bookingTypeId?: number;
  serviceLevel?: ServiceLevel;
  pickupFilter?: AgentPickupDateFilter;
}

const BOOKING_STATUS_KEYS = AGENT_BOOKING_STATUS_TABS.map((t) => t.key);

const emptyFilters = (): AgentBookingFilterState => ({
  status: "all",
  q: "",
});

export const useAgentBookingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { agentId, agent, loading: profileLoading } = useAgentProfile();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AgentBookingFilterState>(emptyFilters);
  const [searchMode, setSearchMode] = useState<SearchMode>("none");
  const [listTruncated, setListTruncated] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>(
    []
  );
  const [transportTypes, setTransportTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [bookingTypes, setBookingTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [containers, setContainers] = useState<AgentContainerOption[]>([]);
  const [masters, setMasters] = useState<
    { id: number; reference: string | null }[]
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [statusCounts, setStatusCounts] = useState<
    Record<(typeof AGENT_LIST_BOOKING_STATUSES)[number], number>
  >({} as Record<(typeof AGENT_LIST_BOOKING_STATUSES)[number], number>);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const urlInitRef = useRef(false);
  const loadSeqRef = useRef(0);
  const filtersFromUrlRef = useRef("");

  const syncUrl = useCallback(
    (next: AgentBookingFilterState) => {
      setUrlParams(setSearchParams, {
        status: next.status === "all" ? undefined : next.status,
        q: next.q.trim() || undefined,
        company: next.companyId,
        transport: next.transportTypeId,
        hasContainer: next.hasContainer,
        container: next.containerId,
        master: next.masterContainerId,
        bookingType: next.bookingTypeId,
        serviceLevel: next.serviceLevel,
        pickup: next.pickupFilter,
      });
    },
    [setSearchParams]
  );

  const loadStatusCounts = useCallback(async () => {
    if (!agentId) return;
    const { data } = await fetchAgentBookingStatusCounts(agentId);
    setStatusCounts(data);
  }, [agentId]);

  const loadBookings = useCallback(
    async (next: AgentBookingFilterState) => {
      if (!agentId) return;

      const seq = ++loadSeqRef.current;
      setLoading(true);
      // Keep previous rows visible while loading (feels faster, less flicker)

      const bookingId = parseAgentBookingIdSearch(next.q);
      if (bookingId != null) {
        setSearchMode("id");
        const { data, error } = await fetchAgentBookingById(agentId, bookingId);
        if (seq !== loadSeqRef.current) return;
        if (error) {
          message.error("Failed to search booking");
          setBookings([]);
        } else if (!data) {
          message.info(`Booking ${bookingId} not found for your agent`);
          setBookings([]);
        } else {
          setBookings([data]);
        }
        setListTruncated(false);
        setLoading(false);
        return;
      }

      setSearchMode("none");

      // Any active filter must load every matching booking (not the capped "All" tab set).
      // Text search is applied client-side, so we still need the full candidate set.
      const fetchAllMatching =
        (next.status != null && next.status !== "all") ||
        next.companyId != null ||
        next.transportTypeId != null ||
        next.containerId != null ||
        next.hasContainer != null ||
        next.masterContainerId != null ||
        next.bookingTypeId != null ||
        next.serviceLevel != null ||
        !!next.pickupFilter ||
        !!next.q.trim();

      const { data, error, truncated } = await fetchAgentBookings(agentId, {
        statusFilter: next.status,
        companyId: next.companyId,
        transportTypeId: next.transportTypeId,
        containerId: next.containerId,
        hasContainer: next.hasContainer,
        masterContainerId: next.masterContainerId,
        bookingTypeId: next.bookingTypeId,
        serviceLevel: next.serviceLevel,
        fetchAllMatching,
      });
      if (seq !== loadSeqRef.current) return;
      if (error) message.error("Failed to load bookings");
      setBookings(data);
      setListTruncated(truncated ?? false);
      setLoading(false);
    },
    [agentId]
  );

  const parseFiltersFromUrl = useCallback((): AgentBookingFilterState => {
    return {
      status: parseUrlStatus(searchParams.get("status"), BOOKING_STATUS_KEYS),
      q: searchParams.get("q") || "",
      companyId: parseUrlInt(searchParams.get("company")),
      transportTypeId: parseUrlInt(searchParams.get("transport")),
      hasContainer: parseUrlHasContainer(searchParams.get("hasContainer")),
      containerId: parseUrlInt(searchParams.get("container")),
      masterContainerId: parseUrlInt(searchParams.get("master")),
      bookingTypeId: parseUrlInt(searchParams.get("bookingType")),
      serviceLevel: parseUrlServiceLevel(searchParams.get("serviceLevel")),
      pickupFilter: parseUrlPickupFilter(searchParams.get("pickup")),
    };
  }, [searchParams]);

  const serializeFilters = (f: AgentBookingFilterState) => JSON.stringify(f);

  const applyFilters = useCallback(
    (next: AgentBookingFilterState) => {
      filtersFromUrlRef.current = serializeFilters(next);
      setFilters(next);
      syncUrl(next);
      return loadBookings(next);
    },
    [loadBookings, syncUrl]
  );

  useEffect(() => {
    if (profileLoading || !agentId) return;

    const fromUrl = parseFiltersFromUrl();
    const serialized = serializeFilters(fromUrl);

    if (!urlInitRef.current) {
      urlInitRef.current = true;
      filtersFromUrlRef.current = serialized;
      setFilters(fromUrl);
      loadBookings(fromUrl);
      loadStatusCounts();
      return;
    }

    if (serialized !== filtersFromUrlRef.current) {
      filtersFromUrlRef.current = serialized;
      setFilters(fromUrl);
      setSelectedRowKeys([]);
      loadBookings(fromUrl);
    }
  }, [
    profileLoading,
    agentId,
    searchParams,
    parseFiltersFromUrl,
    loadBookings,
    loadStatusCounts,
  ]);

  useEffect(() => {
    if (!agentId) return;
    setLoadingOptions(true);
    Promise.all([
      fetchAgentBookingCompanyOptions(agentId),
      fetchAgentContainerOptions(),
      fetchMasterContainersForFilter({ agentId }),
      supabase
        .from("booking_transport_types")
        .select("id, name")
        .order("name"),
      supabase.from("bookings_type").select("id, name").order("name"),
    ]).then(
      ([
        companiesResult,
        containersResult,
        mastersResult,
        transportResult,
        bookingTypesResult,
      ]) => {
        if (companiesResult.error) {
          console.error("Failed to load company filter options:", companiesResult.error);
          message.error("Failed to load company filter options");
        }
        setCompanies(companiesResult.data);
        setContainers(containersResult.data);
        setMasters(
          (mastersResult.data || []).map((m) => ({
            id: m.id,
            reference: m.reference,
          }))
        );
        setTransportTypes(transportResult.data || []);
        setBookingTypes(bookingTypesResult.data || []);
        setLoadingOptions(false);
      }
    );
  }, [agentId]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const hasActiveFilters =
    !!filters.q.trim() ||
    filters.companyId != null ||
    filters.transportTypeId != null ||
    filters.hasContainer != null ||
    filters.containerId != null ||
    filters.masterContainerId != null ||
    filters.bookingTypeId != null ||
    filters.serviceLevel != null ||
    filters.pickupFilter != null;

  const handleStatusChange = (status: string) => {
    setSelectedRowKeys([]);
    applyFilters({ ...filters, status });
  };

  const handleSearchChange = (value: string) => {
    const next = { ...filters, q: value };
    filtersFromUrlRef.current = serializeFilters(next);
    setFilters(next);
    syncUrl(next);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(() => {
      loadBookings(next);
    }, 300);
  };

  const handleCompanyChange = (companyId?: number) => {
    setSelectedRowKeys([]);
    applyFilters({ ...filters, companyId });
  };

  const handleTransportChange = (transportTypeId?: number) => {
    setSelectedRowKeys([]);
    applyFilters({ ...filters, transportTypeId });
  };

  const handleHasContainerChange = (hasContainer?: AgentHasContainerFilter) => {
    setSelectedRowKeys([]);
    applyFilters({
      ...filters,
      hasContainer,
      containerId: hasContainer === "no" ? undefined : filters.containerId,
    });
  };

  const handleContainerChange = (containerId?: number) => {
    setSelectedRowKeys([]);
    applyFilters({
      ...filters,
      containerId,
      hasContainer: containerId != null ? undefined : filters.hasContainer,
      masterContainerId:
        containerId != null ? undefined : filters.masterContainerId,
    });
  };

  const handleMasterContainerChange = (masterContainerId?: number) => {
    setSelectedRowKeys([]);
    applyFilters({
      ...filters,
      masterContainerId,
      // Master implies containers; clear conflicting container filters
      containerId:
        masterContainerId != null ? undefined : filters.containerId,
      hasContainer:
        masterContainerId != null ? undefined : filters.hasContainer,
    });
  };

  const handleBookingTypeChange = (bookingTypeId?: number) => {
    setSelectedRowKeys([]);
    applyFilters({ ...filters, bookingTypeId });
  };

  const handleServiceLevelChange = (serviceLevel?: ServiceLevel) => {
    setSelectedRowKeys([]);
    applyFilters({ ...filters, serviceLevel });
  };

  const handlePickupFilterChange = (pickupFilter?: AgentPickupDateFilter) => {
    setSelectedRowKeys([]);
    applyFilters({ ...filters, pickupFilter });
  };

  const handleClearFilters = () => {
    setSelectedRowKeys([]);
    applyFilters({ status: filters.status, q: "" });
  };

  const handleRefresh = () => {
    setSelectedRowKeys([]);
    applyFilters({ status: filters.status, q: "" });
    loadStatusCounts();
  };

  const reloadAfterMutation = async () => {
    await loadBookings(filters);
    await loadStatusCounts();
  };

  return {
    agent,
    profileLoading,
    bookings,
    loading,
    filters,
    searchMode,
    listTruncated,
    selectedRowKeys,
    setSelectedRowKeys,
    companies,
    transportTypes,
    bookingTypes,
    containers,
    masters,
    loadingOptions,
    hasActiveFilters,
    statusCounts,
    handleStatusChange,
    handleSearchChange,
    handleCompanyChange,
    handleTransportChange,
    handleHasContainerChange,
    handleContainerChange,
    handleMasterContainerChange,
    handleBookingTypeChange,
    handleServiceLevelChange,
    handlePickupFilterChange,
    handleClearFilters,
    handleRefresh,
    reloadAfterMutation,
    agentId,
  };
};
